import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import type { ApiResponse, User } from '@tipstrack/shared';
import { userManager } from '../auth/oidc.js';

export const TOKEN_KEY = 'tipstrack_token';
// 'sso' when the active session was established via the TIPSSSO (ZITADEL) portal;
// absent/anything else for a legacy password session. Lives here (not in ssoBridge)
// so the 401 interceptor can read it without an import cycle.
export const AUTH_MODE_KEY = 'tipstrack_auth_mode';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore storage failures (private mode etc.) */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function isSsoSession(): boolean {
  try {
    return localStorage.getItem(AUTH_MODE_KEY) === 'sso';
  } catch {
    return false;
  }
}

export function clearSsoMarker(): void {
  try {
    localStorage.removeItem(AUTH_MODE_KEY);
  } catch {
    /* ignore */
  }
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    // config.headers is an AxiosHeaders instance in axios v1; .set is the typed mutator.
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// One-shot 401 guard. Several in-flight requests can 401 together (dashboard fires a
// handful of queries); without the flag each rejection would clear state and issue its
// own redirect. Worse, an SSO session that 401s, reloads, re-bridges the (still-bad)
// OIDC token and 401s again would loop forever — so on an SSO 401 we also remove the
// stored OIDC user before redirecting to the login page.
let handling401 = false;

api.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError) => {
    if (error.response?.status === 401 && !handling401) {
      handling401 = true;
      const sso = isSsoSession();
      clearToken();
      clearSsoMarker();
      if (sso && userManager) {
        void userManager.removeUser().catch(() => {});
      }
      const path = window.location.pathname;
      // Avoid redirect loops on the login page / mid OIDC callback.
      if (!path.startsWith('/login') && !path.startsWith('/auth/')) {
        const redirect = encodeURIComponent(path + window.location.search);
        window.location.assign(`/login?redirect=${redirect}`);
      } else {
        handling401 = false;
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Probe `/auth/me` WITHOUT the global 401 interceptor. Used to verify an SSO session
 * at login: on success the bearer maps to a real TipsTrack user; on failure (e.g. a
 * ZITADEL identity that isn't provisioned) the caller signs out cleanly instead of
 * fabricating a session — which would cause a 401 → redirect → re-bridge reload loop.
 */
export async function fetchMeRaw(): Promise<User> {
  const token = getToken();
  const res = await axios.get<ApiResponse<User>>(`${api.defaults.baseURL ?? '/api'}/auth/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const body = res.data;
  if (!body.success) {
    throw new Error(body.message || body.error || 'Not authorized');
  }
  return body.data;
}

/** Normalize an axios/error into a human-readable message. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiResponse<unknown> | undefined;
    if (data && !data.success) return data.message || data.error || err.message;
    return err.message || 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}

// ---- Envelope unwrap helpers ------------------------------------------------
// The server replies with { success, data } (ApiResponse<T>); these unwrap `data`.

async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const resp = await promise;
  const body = resp.data;
  if (!body.success) {
    throw new Error(body.message || body.error || 'Request failed');
  }
  return body.data;
}

export function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.get<ApiResponse<T>>(url, config));
}

export function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.post<ApiResponse<T>>(url, body, config));
}

export function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.patch<ApiResponse<T>>(url, body, config));
}

export function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.delete<ApiResponse<T>>(url, config));
}
